import { 
  LayoutDashboard, 
  Music, 
  Users, 
  Settings, 
  FileText, 
  BarChart3,
  TrendingUp,
  Sparkles,
  Calendar,
  CreditCard,
  Share2,
  Sun,
  Moon,
  MessageSquare,
  LogOut,
  Mic,
  Clock,
  Ticket,
  Eye,
  Star,
  HelpCircle,
  FileText as FileTextIcon,
  Gift,
  Play,
  Activity,
  HardDrive,
  Megaphone
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useAdminTheme } from "@/contexts/AdminThemeContext";
import type { User } from "@supabase/supabase-js";

interface NavSection {
  label?: string;
  items: { path: string; label: string; icon: any }[];
}

const navSections: NavSection[] = [
  {
    items: [
      { path: '/admin', label: 'Дашборд', icon: LayoutDashboard },
    ],
  },
  {
    label: 'Данные',
    items: [
      { path: '/admin/generations', label: 'Генерации', icon: Music },
      { path: '/admin/users', label: 'Пользователи', icon: Users },
      { path: '/admin/transactions', label: 'Транзакции', icon: CreditCard },
      { path: '/admin/referrals', label: 'Рефералы', icon: Share2 },
      { path: '/admin/feedback', label: 'Обратная связь', icon: MessageSquare },
      { path: '/admin/timings', label: 'Время генерации', icon: Clock },
    ],
  },
  {
    label: 'Контент',
    items: [
      { path: '/admin/events', label: 'События', icon: Calendar },
      { path: '/admin/genres', label: 'Жанры', icon: Music },
      { path: '/admin/vocals', label: 'Вокалы', icon: Mic },
      { path: '/admin/prompts', label: 'Промпты', icon: FileText },
      { path: '/admin/promo-codes', label: 'Промокоды', icon: Ticket },
      { path: '/admin/gift-pages', label: 'Gift-страницы', icon: Gift },
      { path: '/admin/gift-analytics', label: 'Gift-аналитика', icon: TrendingUp },
      { path: '/admin/post-purchase', label: 'Post-Purchase', icon: MessageSquare },
    ],
  },
  {
    label: 'Лендинг',
    items: [
      { path: '/admin/landing-reviews', label: 'Отзывы', icon: Star },
      { path: '/admin/landing-faq', label: 'FAQ', icon: HelpCircle },
      { path: '/admin/landing-seo', label: 'SEO-тексты', icon: FileTextIcon },
    ],
  },
  {
    label: 'Офлайн',
    items: [
      { path: '/admin/qr-campaigns', label: 'QR-кампании', icon: Eye },
    ],
  },
  {
    label: 'Маркетинг',
    items: [
      { path: '/admin/broadcasts', label: 'Рассылки', icon: Megaphone },
    ],
  },
  {
    label: 'Инструменты',
    items: [
      { path: '/admin/bot-simulator', label: 'Симулятор бота', icon: Play },
      { path: '/admin/audio-pipeline', label: 'Audio Pipeline', icon: Activity },
      { path: '/admin/storage', label: 'Storage', icon: HardDrive },
    ],
  },
  {
    label: 'Настройки',
    items: [
      { path: '/admin/analytics', label: 'Пиксели и трекинг', icon: Eye },
      { path: '/admin/settings', label: 'Настройки', icon: Settings },
    ],
  },
];

export function AdminSidebar() {
  const location = useLocation();
  const { theme, toggleTheme } = useAdminTheme();

  return (
    <aside className="fixed left-0 top-0 bottom-0 w-64 bg-card border-r border-border z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-2 p-4 border-b border-border flex-shrink-0">
        <Sparkles className="w-5 h-5 text-primary" />
        <span className="font-display text-lg font-bold">Хит Шеф</span>
      </div>

      {/* Navigation - scrollable */}
      <nav className="flex-1 overflow-y-auto p-2 space-y-1 min-h-0">
        {navSections.map((section, si) => (
          <div key={si}>
            {section.label && (
              <p className="px-3 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
                {section.label}
              </p>
            )}
            {section.items.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link key={item.path} to={item.path}>
                  <Button
                    variant={isActive ? "secondary" : "ghost"}
                    size="sm"
                    className={cn(
                      "w-full justify-start gap-2 h-9",
                      !isActive && "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    <item.icon className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate text-sm">{item.label}</span>
                  </Button>
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Footer - always visible */}
      <div className="p-3 border-t border-border space-y-2 flex-shrink-0">
        {/* Theme toggle */}
        <div className="flex items-center justify-between px-2 py-1.5 rounded-md bg-muted/50">
          <div className="flex items-center gap-2">
            {theme === "light" ? (
              <Sun className="w-3.5 h-3.5 text-muted-foreground" />
            ) : (
              <Moon className="w-3.5 h-3.5 text-muted-foreground" />
            )}
            <span className="text-xs text-muted-foreground">
              {theme === "light" ? "Светлая" : "Тёмная"}
            </span>
          </div>
          <Switch
            checked={theme === "dark"}
            onCheckedChange={toggleTheme}
            className="scale-90"
          />
        </div>
        
        <Link to="/">
          <Button variant="ghost" size="sm" className="w-full h-8 text-xs text-muted-foreground">
            ← На сайт
          </Button>
        </Link>
      </div>
    </aside>
  );
}